import { useEffect, useRef, useState } from 'react';
import { Download, X } from 'lucide-react';
import { toast } from 'sonner';
import { goPublicUrl } from '../../lib/goLinks';
import {
  GO_QR_INK_SWATCHES,
  GO_QR_LOGO_SCALE_MAX,
  GO_QR_LOGO_SCALE_MIN,
  downloadCanvasPng,
  readSavedGoQrLogo,
  readSavedGoQrStyle,
  renderGoQrCanvas,
  saveGoQrLogo,
  saveGoQrStyle,
  type GoQrLogoChoice,
  type GoQrStyle,
} from '../../lib/goQr';
import GoQrLogoPicker from './GoQrLogoPicker';

export default function GoQrModal({
  slug,
  onClose,
}: {
  slug: string;
  onClose: () => void;
}) {
  const previewRef = useRef<HTMLCanvasElement>(null);
  const fullRef = useRef<HTMLCanvasElement | null>(null);
  const [withLogo, setWithLogo] = useState(true);
  const [picking, setPicking] = useState(false);
  const [logo, setLogo] = useState<GoQrLogoChoice>(() => readSavedGoQrLogo());
  const [style, setStyle] = useState<GoQrStyle>(() => readSavedGoQrStyle());
  const [logoNote, setLogoNote] = useState<string | null>(null);
  const [drawing, setDrawing] = useState(true);
  const url = goPublicUrl(slug);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    setDrawing(true);
    void (async () => {
      try {
        setLogoNote(null);
        const canvas = await renderGoQrCanvas(url, {
          withLogo,
          logoSrc: logo.src,
          dark: style.dark,
          logoScale: style.logoScale,
        });
        if (cancelled) return;
        fullRef.current = canvas;
        const preview = previewRef.current;
        if (preview) {
          preview.width = canvas.width;
          preview.height = canvas.height;
          preview.getContext('2d')?.drawImage(canvas, 0, 0);
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Could not draw QR';
        if (withLogo && logo.src !== '/go-qr-logo.png') {
          try {
            const fallback = await renderGoQrCanvas(url, {
              withLogo: true,
              logoSrc: '/go-qr-logo.png',
              dark: style.dark,
              logoScale: style.logoScale,
            });
            if (cancelled) return;
            fullRef.current = fallback;
            const preview = previewRef.current;
            if (preview) {
              preview.width = fallback.width;
              preview.height = fallback.height;
              preview.getContext('2d')?.drawImage(fallback, 0, 0);
            }
            setLogoNote('Could not stamp that mark — using the default for download. Try From file.');
            return;
          } catch {
            /* fall through */
          }
        }
        toast.error(message);
      } finally {
        if (!cancelled) setDrawing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [url, withLogo, logo.src, style.dark, style.logoScale]);

  const download = () => {
    const canvas = fullRef.current;
    if (!canvas) return;
    downloadCanvasPng(canvas, `go-${slug}.png`);
    toast.success(`Downloaded go-${slug}.png`);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center p-0 sm:p-4 bg-black/50">
      <div
        className="bg-white dark:bg-gray-800 shadow-xl w-full max-w-lg flex flex-col min-h-0 h-[100dvh] max-h-[100dvh] rounded-none sm:h-auto sm:max-h-[min(90vh,900px)] sm:rounded-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="go-qr-title"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <h2 id="go-qr-title" className="text-lg font-semibold text-gray-900 dark:text-white">
            QR for /{slug}
          </h2>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1 min-h-0 space-y-3">
          <p className="break-all font-mono text-xs text-gray-600 dark:text-gray-300">{url}</p>
          <div className="flex justify-center rounded-xl border border-gray-200 dark:border-gray-700 bg-white p-3">
            <canvas
              ref={previewRef}
              className="w-64 h-64 max-w-full"
              style={{ imageRendering: 'pixelated' }}
            />
          </div>
          <div className="flex items-center justify-between gap-3">
            <label className="text-sm text-gray-700 dark:text-gray-200">Ink</label>
            <div className="flex items-center gap-1.5">
              {GO_QR_INK_SWATCHES.map((hex) => (
                <button
                  key={hex}
                  type="button"
                  aria-label={`Ink ${hex}`}
                  onClick={() => {
                    const next = { ...style, dark: hex };
                    setStyle(next);
                    saveGoQrStyle(next);
                  }}
                  className={`h-6 w-6 rounded-full border ${
                    style.dark === hex ? 'border-emerald-500 ring-2 ring-emerald-400/40' : 'border-gray-300 dark:border-gray-600'
                  }`}
                  style={{ backgroundColor: hex }}
                />
              ))}
              <input
                type="color"
                value={style.dark}
                aria-label="Custom ink"
                onChange={(e) => {
                  const next = { ...style, dark: e.target.value };
                  setStyle(next);
                  saveGoQrStyle(next);
                }}
                className="h-7 w-8 cursor-pointer rounded border border-gray-300 dark:border-gray-600 bg-transparent"
              />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
            <input
              type="checkbox"
              checked={withLogo}
              onChange={(e) => setWithLogo(e.target.checked)}
              className="rounded border-gray-300"
            />
            Center logo
          </label>
          {withLogo ? (
            <>
              <label className="block text-sm text-gray-700 dark:text-gray-200">
                Mark size: {Math.round(style.logoScale * 100)}%
                <input
                  type="range"
                  min={Math.round(GO_QR_LOGO_SCALE_MIN * 100)}
                  max={Math.round(GO_QR_LOGO_SCALE_MAX * 100)}
                  step={1}
                  value={Math.round(style.logoScale * 100)}
                  onChange={(e) => {
                    const next = { ...style, logoScale: Number(e.target.value) / 100 };
                    setStyle(next);
                    saveGoQrStyle(next);
                  }}
                  className="w-full mt-1 accent-emerald-500"
                />
              </label>
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  Mark: {logo.label}
                </p>
                <button
                  type="button"
                  onClick={() => setPicking((v) => !v)}
                  className="text-xs text-emerald-700 dark:text-emerald-300 hover:underline"
                >
                  {picking ? 'Hide picker' : 'Change mark'}
                </button>
              </div>
              {picking ? (
                <GoQrLogoPicker
                  current={logo}
                  onPick={(choice) => {
                    setLogo(choice);
                    saveGoQrLogo(choice);
                    setPicking(false);
                  }}
                />
              ) : null}
            </>
          ) : null}
          {logoNote ? (
            <p className="text-xs text-amber-700 dark:text-amber-300">{logoNote}</p>
          ) : null}
          {drawing ? (
            <p className="text-xs text-gray-500">Drawing…</p>
          ) : (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Error correction H so the center mark still scans. Print at least ~1.2&quot; square.
            </p>
          )}
        </div>

        <div className="shrink-0 flex justify-end gap-2 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600"
          >
            Close
          </button>
          <button
            type="button"
            onClick={download}
            disabled={drawing}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            Download PNG
          </button>
        </div>
      </div>
    </div>
  );
}
